import { CustomEncoder } from './custom-encoder';
import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { timeout } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

export interface HttpOptions {
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
      };
  context?: HttpContext;
  observe?: 'body';
  params?:
    | HttpParams
    | {
        [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
      };
  reportProgress?: boolean;
  responseType?: 'json';
  withCredentials?: boolean;
  transferCache?:
    | {
        includeHeaders?: string[];
      }
    | boolean;
}

interface ApiErrorResponse {
  id: string;
  message: string;
  details: string;
  errorCode: string;
  additionalData?: { [key: string]: string };
}

export function isApiErrorResponse(obj: any): obj is ApiErrorResponse {
  return obj && typeof obj === 'object' && 'id' in obj && 'errorCode' in obj && 'message' in obj;
}

@Injectable()
export class ApiService {
  constructor(private _http: HttpClient) {}

  // --------------------------------------------------------------------------------------------------------------
  // ------------------------------------------------ services with token for backend api -------------------------
  // --------------------------------------------------------------------------------------------------------------

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post(url: string, data): Observable<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post<T>(url: string, body?: any | null, options?: object): Observable<T>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post<T>(url: string, body: any | null = null, options?: object): Observable<T> {
    return this._http.post<T>(url, body, options);
  }

  postWithParams(url: string, data: any, params: object): Observable<any> {
    const queryParams = this._customQueryParams(params);
    return this._http.post(url, data, {
      params: queryParams,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  put(url: string, data): Observable<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  put<T>(url: string, body?: any | null, options?: object): Observable<T>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  put<T>(url: string, body: any | null = null, options?: object): Observable<T> {
    return this._http.put<T>(url, body, options).pipe(timeout(30000));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get(url: string): Observable<any>;
  get<T>(url: string, options?: HttpOptions): Observable<T>;
  get<T>(url: string, options?: HttpOptions): Observable<T> {
    return this._http.get<T>(url, options);
  }

  delete(url: string, options?: object): Observable<any> {
    return this._http.delete(url, options);
  }

  //
  // FUNCTION postWithTokenFormData NEVER USED FOR API CALL
  //
  postWithTokenFormData(url, data) {
    const formData = new FormData();
    formData.append('data', JSON.stringify(data));
    const result = this._http.post(url, formData);
    return result;
  }

  postBlob(url: string, blob: Blob): Observable<any> {
    const formData = new FormData();
    formData.append('file', blob);
    return this._http.post(url, formData);
  }

  postWithTokenFileDownload(url, data, timeoutMs: number = 30000) {
    //TKM Changed response type to blob.
    const result = this._http.post(url, data, { responseType: 'blob' as 'json' }).pipe(
      timeout(timeoutMs),
      catchError((error: unknown) => {
        return throwError(error);
      }),
    );
    return result;
  }
  /**
   This will allow posting a simple value as [FromBody] parameter
  */
  postWithSimpleBody(url: string, data: string | number): Observable<any> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this._http.post(url, JSON.stringify(data), options);
  }

  getWithParams(
    url,
    params,
    httpHeaders: HttpHeaders = null,
    responseType = null,
  ): Observable<any> {
    const queryParams = this._customQueryParams(params);

    return !httpHeaders && !responseType
      ? this._http.get(url, { params: queryParams })
      : this._http.get(url, {
          headers: httpHeaders,
          params: queryParams,
          responseType: responseType,
          observe: 'response',
        });
  }

  // custom encoding created for '+' sign, currently broken
  // https://github.com/angular/angular/issues/11058
  private _customQueryParams(params: any) {
    let httpParams = new HttpParams({
      encoder: new CustomEncoder(),
    });

    Object.keys(params).forEach((key) => {
      if (Array.isArray(params[key])) {
        Object.keys(params[key]).forEach((k) => {
          httpParams = httpParams.append(key, params[key][k]);
        });
      } else {
        httpParams = httpParams.append(key, params[key]);
      }
    });

    return httpParams;
  }
}
